import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Users,
  Search,
  Download,
  CheckCircle2,
  Circle,
  Ticket,
  Calendar,
  Mail,
  Send,
  X,
} from "lucide-react";
import PortalShell from "@/components/PortalShell";
import { portalApi, portalMe } from "@/lib/portalApi";

const AUDIENCE_LABEL = {
  everyone: "Everyone",
  couples: "Couples",
  tv: "TV",
  single_guys: "Single Guys",
  single_ladies: "Single Ladies",
  single_guy: "Single Man",
  single_lady: "Single Lady",
  couple: "Couple",
};

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

export default function PortalGuestlist() {
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [events, setEvents] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);

  const eventId = params.get("event_id") || "";

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, glRes] = await Promise.all([
        portalMe(),
        portalApi.get("/portal/guestlist", {
          params: { event_id: eventId || undefined, q: q.trim() || undefined },
        }),
      ]);
      setMe(meRes);
      setEvents(glRes.data.events || []);
      setAttendees(glRes.data.attendees || []);
    } catch {
      toast.error("Unable to load guestlist.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const setEventId = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("event_id", id);
    else next.delete("event_id");
    setParams(next, { replace: true });
  };

  const selectedEvent = useMemo(
    () => events.find((e) => e.id === eventId) || null,
    [events, eventId]
  );

  const exportCsv = () => {
    if (attendees.length === 0) {
      toast.error("Nothing to export.");
      return;
    }
    const header = [
      "First name",
      "Last name",
      "Email",
      "Phone",
      "FabSwingers",
      "SwingHub",
      "Membership #",
      "Tier",
      "Event",
      "Event date",
      "Event time",
      "Status",
      "Ticket ID",
    ];
    const rows = attendees.map((a) => [
      a.first_name || "",
      a.last_name || "",
      a.email || "",
      a.phone || "",
      a.fabswingers_username || "",
      a.swinghub_profile || "",
      a.membership_number || "",
      AUDIENCE_LABEL[a.tier] || a.tier || "",
      a.event_name || "",
      a.event_date || "",
      a.event_time || "",
      a.status || "",
      a.ticket_id || "",
    ]);
    const csv = [header, ...rows]
      .map((r) =>
        r
          .map((c) => {
            const s = String(c ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    const suffix = selectedEvent ? `-${selectedEvent.slug}` : "";
    a.download = `guestlist${suffix}-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PortalShell me={me} testId="portal-guestlist-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-10">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">
              Attendees
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Guestlists</h1>
            <p className="mt-3 text-sm text-[#A3A3A3] max-w-xl">
              Live list of ticket holders across your paid orders. Filter by event and export
              for door staff.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {selectedEvent && (
              <button
                type="button"
                data-testid="guestlist-send-review-open"
                onClick={() => setReviewOpen(true)}
                disabled={loading || attendees.length === 0}
                className="inline-flex items-center gap-2 border border-[#D9A6F5] text-[#D9A6F5] hover:bg-[#D9A6F5] hover:text-black disabled:opacity-40 disabled:cursor-not-allowed px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
              >
                <Mail size={13} /> Send review request
              </button>
            )}
            <button
              type="button"
              data-testid="guestlist-export"
              onClick={exportCsv}
              disabled={loading || attendees.length === 0}
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] disabled:opacity-40 disabled:cursor-not-allowed px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Download size={13} /> Export CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3 mb-6" data-testid="guestlist-filters">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
            <input
              type="text"
              placeholder="Search name, email or phone"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="guestlist-search"
              className="w-full bg-black border border-white/15 h-11 pl-10 pr-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5] text-sm"
            />
          </div>
          <select
            value={eventId}
            data-testid="guestlist-event-filter"
            onChange={(e) => setEventId(e.target.value)}
            className="bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5] text-sm"
          >
            <option value="">All events ({events.length})</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.name} · {fmtDate(ev.first_date)}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="py-24 flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
          </div>
        ) : attendees.length === 0 ? (
          <div className="text-center py-24">
            <div className="inline-flex items-center justify-center h-16 w-16 border border-white/10 mb-6">
              <Users size={22} className="text-[#D9A6F5]" />
            </div>
            <p className="text-[#A3A3A3]">
              {q
                ? "No attendees match your search."
                : selectedEvent
                ? `No attendees yet for ${selectedEvent.name}.`
                : "No paid tickets yet — your first attendee will appear here."}
            </p>
          </div>
        ) : (
          <>
            <div className="mb-3 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]" data-testid="guestlist-count">
              {attendees.length} attendee{attendees.length === 1 ? "" : "s"}
              {selectedEvent ? ` · ${selectedEvent.name}` : ""}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block bg-[#0A0A0A] border border-white/10 overflow-x-auto">
              <div className="grid grid-cols-[1.2fr_1.4fr_1fr_1.1fr_1.1fr_0.8fr_1.2fr_90px] gap-4 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.22em] text-[#D9A6F5] min-w-[1100px]">
                <div>Name</div>
                <div>Email</div>
                <div>Phone</div>
                <div>FabSwingers</div>
                <div>SwingHub</div>
                <div>Tier</div>
                <div>Event</div>
                <div>Status</div>
              </div>
              {attendees.map((a) => (
                <div
                  key={a.ticket_id}
                  data-testid={`guestlist-row-${a.ticket_id}`}
                  className="grid grid-cols-[1.2fr_1.4fr_1fr_1.1fr_1.1fr_0.8fr_1.2fr_90px] gap-4 px-5 py-3 border-b border-white/5 last:border-b-0 text-sm items-center min-w-[1100px]"
                >
                  <div className="text-[#F5F5F0] truncate">
                    <div className="truncate">
                      {(a.first_name || "") + " " + (a.last_name || "") || "—"}
                    </div>
                    {a.membership_number && (
                      <div
                        className="text-[9px] uppercase tracking-[0.22em] text-[#D9A6F5] mt-0.5 truncate"
                        data-testid={`guestlist-membership-${a.ticket_id}`}
                      >
                        Member · {a.membership_number}
                      </div>
                    )}
                  </div>
                  <div className="text-[#A3A3A3] truncate">{a.email || "—"}</div>
                  <div className="text-[#A3A3A3] truncate">{a.phone || "—"}</div>
                  <div className="text-[#A3A3A3] truncate" data-testid={`guestlist-fs-${a.ticket_id}`}>
                    {a.fabswingers_username ? (
                      <a
                        href={`https://m.fabswingers.com/profile/${encodeURIComponent(a.fabswingers_username)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#D9A6F5] hover:underline truncate inline-block max-w-full"
                      >
                        {a.fabswingers_username}
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                  <div className="text-[#A3A3A3] truncate" data-testid={`guestlist-sh-${a.ticket_id}`}>
                    {a.swinghub_profile ? (
                      <a
                        href={a.swinghub_profile}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#D9A6F5] hover:underline truncate inline-block max-w-full"
                      >
                        View →
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                  <div className="text-[#A3A3A3] truncate">{AUDIENCE_LABEL[a.tier] || a.tier || "—"}</div>
                  <div className="min-w-0">
                    <div className="text-[#F5F5F0] truncate">{a.event_name}</div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#A3A3A3] truncate">
                      {fmtDate(a.event_date)} · {a.event_time || ""}
                    </div>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-3">
              {attendees.map((a) => (
                <div
                  key={a.ticket_id}
                  data-testid={`guestlist-card-${a.ticket_id}`}
                  className="bg-[#0A0A0A] border border-white/10 p-4"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-base font-bold text-[#F5F5F0] truncate">
                      {(a.first_name || "") + " " + (a.last_name || "") || "—"}
                    </div>
                    <StatusBadge status={a.status} />
                  </div>
                  {a.membership_number && (
                    <div
                      className="mt-1 text-[10px] uppercase tracking-[0.22em] text-[#D9A6F5]"
                      data-testid={`guestlist-membership-card-${a.ticket_id}`}
                    >
                      Member · {a.membership_number}
                    </div>
                  )}
                  <div className="mt-2 text-xs text-[#A3A3A3] break-all">{a.email || "—"}</div>
                  <div className="text-xs text-[#A3A3A3]">{a.phone || "—"}</div>
                  {(a.fabswingers_username || a.swinghub_profile) && (
                    <div className="mt-2 flex flex-col gap-1 text-[11px]">
                      {a.fabswingers_username && (
                        <div className="text-[#A3A3A3]">
                          <span className="text-[#D9A6F5]">FS:</span>{" "}
                          <a
                            href={`https://m.fabswingers.com/profile/${encodeURIComponent(a.fabswingers_username)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#D9A6F5] hover:underline"
                          >
                            {a.fabswingers_username}
                          </a>
                        </div>
                      )}
                      {a.swinghub_profile && (
                        <a
                          href={a.swinghub_profile}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#D9A6F5] hover:underline break-all"
                        >
                          SwingHub profile →
                        </a>
                      )}
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between text-[11px] uppercase tracking-[0.18em]">
                    <span className="inline-flex items-center gap-1.5 text-[#D9A6F5]">
                      <Ticket size={11} /> {AUDIENCE_LABEL[a.tier] || a.tier}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-[#A3A3A3]">
                      <Calendar size={11} /> {fmtDate(a.event_date)}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3] truncate">
                    {a.event_name}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {reviewOpen && selectedEvent && (
        <SendReviewModal
          event={selectedEvent}
          venue={me?.venue}
          attendeesCount={attendees.length}
          onClose={() => setReviewOpen(false)}
          onSent={() => setReviewOpen(false)}
        />
      )}
    </PortalShell>
  );
}

function StatusBadge({ status }) {
  const used = status === "used";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1 border ${
        used ? "border-[#D9A6F5] bg-[#D9A6F5]/10 text-[#D9A6F5]" : "border-white/30 text-[#A3A3A3]"
      }`}
      data-testid={`status-${status || "valid"}`}
    >
      {used ? <CheckCircle2 size={10} /> : <Circle size={10} />}
      {used ? "Checked In" : "Not Yet"}
    </span>
  );
}


function SendReviewModal({ event, venue, attendeesCount, onClose, onSent }) {
  const [url, setUrl] = useState(venue?.review_url || "");
  const [sending, setSending] = useState(false);
  const [saveDefault, setSaveDefault] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (trimmed.length < 4) {
      toast.error("Please paste your review destination link.");
      return;
    }
    setSending(true);
    try {
      const { data } = await portalApi.post(
        `/portal/events/${event.id}/send-review-request`,
        { review_url: trimmed, save_default: saveDefault }
      );
      if (data.sent === 0) {
        toast.warning("No emails sent — attendees are missing email addresses.");
      } else {
        toast.success(
          `Sent to ${data.sent} attendee${data.sent === 1 ? "" : "s"}.`
        );
      }
      onSent?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't send the review request."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="review-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-lg bg-[#0A0A0A] border border-white/15 p-6 md:p-8 relative"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="review-modal-close"
          className="absolute top-3 right-3 text-[#A3A3A3] hover:text-[#F5F5F0] p-2"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3 flex items-center gap-2">
          <Mail size={12} /> Post-event follow up
        </div>
        <h2 className="text-2xl md:text-3xl font-black tracking-tighter text-[#F5F5F0]">
          Send review request
        </h2>
        <p className="mt-3 text-sm text-[#A3A3A3] leading-relaxed">
          Emailing <span className="text-[#F5F5F0] font-bold">{attendeesCount}</span>{" "}
          attendee{attendeesCount === 1 ? "" : "s"} of{" "}
          <span className="text-[#F5F5F0] font-bold">{event.name}</span>. Paste
          the link you want them to review you on (Google, Trustpilot,
          FabSwingers, Facebook, etc.).
        </p>

        <label className="block mt-6 text-[10px] uppercase tracking-[0.22em] text-[#D9A6F5] mb-2">
          Review destination link
        </label>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://g.page/r/your-google-review-link"
          data-testid="review-url-input"
          required
          autoFocus
          className="w-full bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5] text-sm"
        />

        <label className="mt-4 flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={saveDefault}
            onChange={(e) => setSaveDefault(e.target.checked)}
            data-testid="review-save-default"
            className="accent-[#D9A6F5]"
          />
          Remember this link for next time
        </label>

        <div className="mt-6 border-t border-white/10 pt-5 text-[11px] text-[#A3A3A3] leading-relaxed">
          <div className="uppercase tracking-[0.22em] text-[#D9A6F5] text-[10px] mb-2">
            Preview
          </div>
          <p className="text-[#F5F5F0]">Hi [first name]</p>
          <p>
            We hope you had a great time at <em>{event.name}</em> at{" "}
            {venue?.name || "your venue"}.
          </p>
          <p>It&apos;s really helpful to us if you leave us a review; you can do that here:</p>
          <p className="text-[#D9A6F5] truncate">{url || "(your link here)"}</p>
          <p>Thank you for coming and we hope to see you again soon!</p>
          <p className="text-[#F5F5F0]">The team at {venue?.name || "your venue"}</p>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row-reverse gap-3">
          <button
            type="submit"
            disabled={sending}
            data-testid="review-modal-send"
            className="inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
          >
            {sending ? (
              <>
                <Loader2 size={13} className="animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send size={13} /> Send to {attendeesCount}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 text-[#F5F5F0] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
